import React, { FC, useCallback, useState } from 'react';
import { useDataTableColumnConfigurations } from 'utils/hooks/useDataTableColumnConfigurations/useDataTableColumnConfigurations';
import { useDarkly } from 'integrations/launchDarkly/legacy/useDarkly';
import { canDelete } from 'utils/permissions';
import { useStaticPermission, useStaticPermissions } from 'common/auth/Authorize';
import {
  DataTable,
  DataTableBulkActions,
  DataTableToolbar,
  DeleteBulkAction,
  DataTablePaginator
} from '@utmost.co/utmost-design-system';
import { useDialogWithUids, IconButton } from '@beeline.com/bp-design-system';
import { Importer } from '@oc/pages/importers_rsi/Importer';
import { useJobFamilies } from './useJobFamilies';
import { useJobFamilyCells } from './useJobFamilyCells';
import { JobFamilyModal } from './JobFamilyModal';
import { JobFamily } from './jobFamily.graphql';
import { JobFamilyDeleteDialog } from './JobFamilyDeleteDialog';
import { useJobFamiliesIntl } from './translations/useJobFamiliesIntl';
import { ImportEntityTypeModel } from 'src/graphql/types';
import { JobFamiliesExporter } from '@oc/pages/settings/Engagements/JobCatalogues/jobFamilies/JobFamiliesExporter';

export const JobFamilyTable: FC<React.PropsWithChildren<unknown>> = () => {
  const intl = useJobFamiliesIntl();
  const [selectedJobFamilies, setSelectedJobFamilies] = useState<JobFamily[]>([]);
  const [selectedWDJobFamilies, setSelectedWDJobFamilies] = useState<JobFamily[]>([]);
  const [isBulkAction, setIsBulkAction] = useState<boolean>(false);
  const { extendJobFamilyGroup } = useDarkly();

  const { columnConfiguration, patchColumnConfiguration } = useDataTableColumnConfigurations('ENGAGEMENTS_JOB_FAMILY');

  const staticPermissions = useStaticPermissions();
  const [showJobFamilyImport, setJobFamilyImport] = useState(false);
  const importJobFamilyEnabled = staticPermissions({ permissionName: 'JOB_FAMILY' }).IMPORT;

  const { remove, getFamilies, jobFamilies, totalCount, setVariables, variables, loading } = useJobFamilies();
  const familyPermissions = useStaticPermission('JOB_FAMILY', true);
  const resultMessage = loading
    ? intl.loading
    : intl.viewingXJobFamilies({
        totalCount,
        pageDataCount: jobFamilies.length
      });
  const deleteDialog = useDialogWithUids([]);

  const [addModalOpen, setAddModal] = useState<boolean>(false);
  const [selectedJobFamily, setSelectedJobFamily] = useState<JobFamily | null>(null);

  const handleShowDeleteDialog = useCallback(
    (uids: string[]) => {
      setIsBulkAction(false);
      deleteDialog.show(uids);
    },
    [deleteDialog]
  );

  const columns = useJobFamilyCells(handleShowDeleteDialog, setSelectedJobFamily);

  const handleDeleteAll = useCallback(
    async (uids: string[]) => {
      await remove(uids);
      getFamilies({ variables: { query: variables?.query, page: variables?.page } });
    },
    [remove, variables, getFamilies]
  );

  const handleConfirmDelete = useCallback(async () => {
    await handleDeleteAll(deleteDialog.uids);
    deleteDialog.close();
  }, [handleDeleteAll, deleteDialog]);

  const handleSelectBulkActions = useCallback(async (selectedRows: JobFamily[]) => {
    const erasableFamilies: JobFamily[] = [];
    const selectedWDFamilies: JobFamily[] = [];
    selectedRows.forEach((jobFamily) => {
      if (canDelete(jobFamily.permissions.JOB_FAMILY)) {
        erasableFamilies.push(jobFamily);
      } else {
        selectedWDFamilies.push(jobFamily);
      }
    });
    setSelectedWDJobFamilies(selectedWDFamilies);
    setSelectedJobFamilies(erasableFamilies);
  }, []);

  if (!familyPermissions.READ) {
    return <>{intl.noPermissionToViewJobFamilies}</>;
  }

  return (
    <>
      {(addModalOpen || !!selectedJobFamily) && (
        <JobFamilyModal
          addModalOpen
          editableResource={selectedJobFamily}
          setAddModal={(e) => {
            setAddModal(e);
            setSelectedJobFamily(null);
          }}
          onComplete={() => getFamilies({ variables: { query: variables?.query, page: variables?.page } })}
        />
      )}
      <DataTable
        onVariablesChanged={setVariables}
        data={jobFamilies}
        columns={columns}
        isFetching={loading}
        totalCount={totalCount}
        onColumnConfigurationChanged={patchColumnConfiguration}
        initialState={columnConfiguration}
        tableHeight="content"
        selectable={extendJobFamilyGroup && familyPermissions.DELETE ? 'uid' : undefined}
        dataTest="job-families-list"
      >
        <DataTableBulkActions<JobFamily> onSelect={handleSelectBulkActions}>
          <DeleteBulkAction<JobFamily>
            disabled={selectedJobFamilies.length === 0 || !extendJobFamilyGroup}
            disabledMessage={intl.cannotDeleteExternalJobFamilies}
            tooltipText={intl.delete}
            onClick={() => {
              setIsBulkAction(true);
              deleteDialog.show(rowsToUids(selectedJobFamilies));
            }}
          />
        </DataTableBulkActions>
        <DataTableToolbar
          searchProps={{
            value: variables?.query?.search || ''
          }}
          afterActions={
            <>
              <IconButton
                iconName="download"
                dataTest="import_job-family"
                disabled={!importJobFamilyEnabled}
                onClick={() => setJobFamilyImport(true)}
                tooltipText={intl.import}
              />
              {importJobFamilyEnabled && showJobFamilyImport && (
                <Importer
                  allowedImportTypes={[{ id: 'JOB_FAMILY', entityType: ImportEntityTypeModel.JobFamily }]}
                  key="JOB_FAMILY"
                  setModal={setJobFamilyImport}
                />
              )}
            </>
          }
          addButtonProps={
            extendJobFamilyGroup
              ? {
                  onAdd: () => setAddModal(true),
                  disabled: !familyPermissions.CREATE,
                  addDataTest: 'job-families__add-job-family-button'
                }
              : undefined
          }
          message={resultMessage}
        />

        <DataTablePaginator />
        <JobFamiliesExporter />
      </DataTable>
      <JobFamilyDeleteDialog
        show={deleteDialog.visible}
        isLoading={loading}
        isBulkAction={isBulkAction}
        jobFamilies={selectedJobFamilies}
        workdayJobFamilies={selectedWDJobFamilies}
        uids={deleteDialog.uids}
        onSubmit={handleConfirmDelete}
        onClose={() => deleteDialog.close()}
      />
    </>
  );
};

function rowsToUids(selectedRows: { uid?: string }[]): string[] {
  return selectedRows.map((row) => row?.uid ?? '');
}
